import React, { useCallback, useEffect, useState } from 'react';
import reconciliationAPI from './services/reconciliationApi';
import { formatApiError } from '../../utils/errorMessage';
import { formatCalendarDay } from '../../utils/localDate';
import { formatMoney } from '../../utils/money';
import { signed, idsToToggle, clearedDateBounds, earlierOpenWarning, notBalancedText } from './PeriodPanel';
import { periodLabel, closingDayText, isCarriedIn, monthBoundaryNote } from './statementDates';

// Reconcile by statement: any start and end date (for example the 16th to the
// 15th). Same steps as a month: enter the statement balances, tick what
// cleared, and mark it reconciled when the difference is 0.00.
export default function StatementPanel({ account, onChanged }) {
  const id = account.account_id;
  const cur = account.currency_code;
  const [overview, setOverview] = useState(null);
  const [day, setDay] = useState('');
  const [draft, setDraft] = useState({ start_date: '', end_date: '', opening_balance: '', closing_balance: '' });
  const [openId, setOpenId] = useState(null);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadOverview = useCallback(async () => {
    const res = await reconciliationAPI.statementOverview(id);
    const d = res.data;
    setOverview(d);
    setDay(d.closing_day ?? '');
    const s = d.suggested || {};
    setDraft({ start_date: s.start_date || '', end_date: s.end_date || '', opening_balance: s.opening_balance ?? '', closing_balance: '' });
  }, [id]);

  useEffect(() => {
    setOpenId(null);
    setView(null);
    loadOverview().catch((err) => setError(formatApiError(err, 'Could not load statements')));
  }, [loadOverview]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      if (res && res.data && res.data.rows) { setView(res.data); setOpenId(res.data.period.id); }
      await loadOverview();
      onChanged();
    } catch (err) {
      setError(formatApiError(err, 'That did not work'));
    } finally {
      setBusy(false);
    }
  };

  const open = (p) => {
    if (p.period_kind !== 'statement') { setError('That period is a calendar month. Open it from the month grid above.'); return; }
    run(() => reconciliationAPI.getStatement(id, p.id));
  };

  if (!overview) return <div className="p-4 text-gray-500">{error || 'Loading...'}</div>;
  const locked = view && view.period.status === 'reconciled';
  const pid = view && view.period.id;
  const picks = view && view.dating_method === 'transaction_date' && !locked;
  const clear = (ids, cleared, dates) => run(() => reconciliationAPI.clearStatementRows(id, pid, ids, cleared, dates));

  return (
    <div className="bg-white rounded shadow p-4 space-y-4">
      <h3 className="text-lg font-semibold">{account.account_name}: reconcile by statement</h3>
      {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}

      <form className="flex flex-wrap items-end gap-2 text-sm" onSubmit={(e) => { e.preventDefault(); run(() => reconciliationAPI.setClosingDay(id, day === '' ? null : Number(day))); }}>
        <label>Statement closing day (account setting)
          <input type="number" min="1" max="31" placeholder="Calendar month" value={day} onChange={(e) => setDay(e.target.value)} className="block border rounded px-2 py-1 w-36" aria-label="Statement closing day" />
        </label>
        <button type="submit" disabled={busy} className="px-3 py-1 border rounded disabled:opacity-50">Save closing day</button>
        <span className="text-xs text-gray-600">{closingDayText(overview.closing_day)}. Leave blank to use calendar months.</span>
      </form>

      <div>
        <h4 className="font-medium text-sm mb-1">Periods for this account</h4>
        {!overview.periods.length && <p className="text-sm text-gray-500">None yet.</p>}
        <ul className="text-sm divide-y border rounded">
          {overview.periods.map((p) => (
            <li key={p.id} className={`flex justify-between px-2 py-1 ${p.id === openId ? 'bg-blue-50' : ''}`}>
              <button type="button" className="text-left underline" onClick={() => open(p)}>{periodLabel(p)}</button>
              <span className="text-gray-600">{p.period_kind === 'month' ? 'Month' : 'Statement'} - {p.status === 'reconciled' ? 'Reconciled' : 'In progress'}</span>
            </li>
          ))}
        </ul>
      </div>

      <form className="flex flex-wrap items-end gap-2 text-sm bg-gray-50 rounded p-2" onSubmit={(e) => { e.preventDefault(); run(() => reconciliationAPI.createStatement(id, draft)); }}>
        <span className="w-full font-medium">New statement</span>
        <p className="w-full text-sm text-blue-800 bg-blue-50 rounded px-2 py-1" role="note">{monthBoundaryNote(overview.last_month_end)}</p>
        {[['start_date', 'Start date', 'date'], ['end_date', 'End date', 'date'], ['opening_balance', 'Opening balance', 'number'], ['closing_balance', 'Ending balance', 'number']].map(([k, label, type]) => (
          <label key={k}>{label}
            <input type={type} step={type === 'number' ? '0.01' : undefined} required value={draft[k] ?? ''} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className="block border rounded px-2 py-1" />
          </label>
        ))}
        <button type="submit" disabled={busy} className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50">Start statement</button>
        <span className="w-full text-xs text-gray-500">Start defaults to the day after your last period ended. Earlier transactions that have not cleared yet are included so you can tick them.</span>
      </form>

      {view && (
        <div className="space-y-2">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-semibold">{periodLabel(view.period)}</h4>
              {locked && <p className="text-sm text-green-700">Reconciled {String(view.period.reconciled_at || '').slice(0, 10)}. Transactions in this period are locked.</p>}
            </div>
            <div className={`text-right ${view.math.balanced ? 'text-green-700' : 'text-red-700'}`}>
              <div className="text-xs">Difference</div><div className="text-2xl font-bold">{formatMoney(view.math.difference, cur)}</div>
              <div className="text-xs text-gray-500">Cleared balance {formatMoney(view.math.cleared_balance, cur)} ({view.math.cleared_count} rows)</div>
            </div>
          </div>
          {!locked && (
            <form className="flex flex-wrap items-end gap-2 text-sm" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.target); run(() => reconciliationAPI.saveStatement(id, pid, Object.fromEntries(f))); }}>
              {[['start_date', 'Start date', 'date', view.period.period_start], ['end_date', 'End date', 'date', view.period.period_end], ['opening_balance', 'Opening balance', 'number', view.period.opening_balance], ['closing_balance', 'Ending balance', 'number', view.period.closing_balance]].map(([k, label, type, value]) => (
                <label key={`${pid}-${k}`}>{label}
                  <input name={k} type={type} step={type === 'number' ? '0.01' : undefined} required defaultValue={value ?? ''} className="block border rounded px-2 py-1" />
                </label>
              ))}
              <button type="submit" disabled={busy} className="px-3 py-1 border rounded disabled:opacity-50">Save</button>
            </form>
          )}
          <div className="flex gap-2 text-sm">
            <button type="button" disabled={busy || locked || !idsToToggle(view.rows, true).length} onClick={() => clear(idsToToggle(view.rows, true), true)} className="px-2 py-1 border rounded disabled:opacity-50">Tick all</button>
            <button type="button" disabled={busy || locked || !idsToToggle(view.rows, false).length} onClick={() => clear(idsToToggle(view.rows, false), false)} className="px-2 py-1 border rounded disabled:opacity-50">Untick all</button>
            <span className="flex-1" />
            {locked
              ? <button type="button" disabled={busy} onClick={() => run(() => reconciliationAPI.reopenStatement(id, pid))} className="px-3 py-1 border border-orange-500 text-orange-700 rounded">Reopen statement</button>
              : <button type="button" disabled={busy || !view.math.balanced} onClick={() => run(() => reconciliationAPI.reconcileStatement(id, pid))} className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-50">Mark reconciled</button>}
          </div>
          {!locked && notBalancedText(view.math, cur, 'this statement') && <p className="text-sm text-red-700" role="status" data-testid="not-balanced">{notBalancedText(view.math, cur, 'this statement')}</p>}
          {!locked && earlierOpenWarning(view.earlier_open_months) && <p className="text-sm text-amber-700" role="status">{earlierOpenWarning(view.earlier_open_months)}</p>}
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="min-w-full text-sm">
              <tbody>
                {view.rows.map((r) => (
                  <tr key={r.id} className={`border-t ${r.cleared ? 'bg-green-50' : ''}`}>
                    <td className="px-2 py-1"><input type="checkbox" checked={r.cleared} disabled={busy || locked} onChange={() => clear([r.id], !r.cleared)} aria-label="Cleared" /></td>
                    <td className="px-2 py-1 whitespace-nowrap">{formatCalendarDay(r.transaction_date)}</td>
                    <td className="px-2 py-1">{r.description}{isCarriedIn(r, view.start) ? ' (carried over)' : ''}
                      {picks && r.cleared ? <input type="date" aria-label="Cleared date" className="ml-2 border rounded px-1 text-xs" disabled={busy} value={r.tick_cleared_date || ''} min={clearedDateBounds(r, view.start, view.end).min} max={view.end} onChange={(e) => e.target.value && clear([r.id], true, { [r.id]: e.target.value })} /> : null}
                      {r.cleared_date ? <span className="ml-1 text-xs text-gray-500">Cleared {formatCalendarDay(r.cleared_date)}</span> : null}</td>
                    <td className="px-2 py-1 text-right font-mono">{signed(r, cur)}</td>
                  </tr>
                ))}
                {!view.rows.length && <tr><td className="p-3 text-gray-500">No transactions for this statement.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
